import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { URL } from "./utils";
import { getAuthTokenHolder, refreshAccessToken } from "./authTokenHolder";

const base = fetchBaseQuery({
  baseUrl: `${URL.url}`,
  credentials: "include",
  fetchFn: async (...args) => fetch(...args),
});

function isAuthEndpoint(url) {
  if (!url) return true;
  const u = typeof url === "string" ? url : url.url || "";
  return (
    u.includes("/api/users/login") ||
    u.includes("/api/users/forgot-password") ||
    u.includes("/api/users/refresh") ||
    u.includes("/api/users/logout") ||
    u.includes("/api/users/verify-2fa") ||
    u.includes("/api/users/send-secondary-code") ||
    u.includes("/api/users/verify-secondary") ||
    u.includes("/api/users/reset-password") ||
    u.includes("/api/users/accept-invitation")
  );
}

/**
 * 401 alındığında önce POST /api/users/refresh (cookie ile) dener;
 * başarılıysa yeni token ile orijinal isteği tekrarlar. Refresh da 401 ise session-expired tetiklenir.
 */
/**
 * Argümandaki Bearer token'ı o anki token ile değiştirir (#142). Sorgu önbellek
 * anahtarı token'sız olduğundan bir sorgu eski token'lı argümanla yeniden
 * çalışabilir (invalidate / polling); istek yine güncel token'la gider.
 */
function withCurrentToken(args) {
  if (!args || typeof args !== "object") return args;
  const current = getAuthTokenHolder()?.getToken?.();
  const auth = args.headers?.Authorization;
  if (!current || typeof auth !== "string" || !auth.startsWith("Bearer ")) return args;
  if (auth === `Bearer ${current}`) return args;
  return { ...args, headers: { ...args.headers, Authorization: `Bearer ${current}` } };
}

export const baseQueryWithReauth = async (rawArgs, api, extraOptions) => {
  const args = withCurrentToken(rawArgs);
  let result = await base(args, api, extraOptions);

  if (result.error) {
    const s = result.error.status;
    const os = result.error.originalStatus;
    // 504, sunucunun bilinçli yanıtıdır (cihaz MQTT timeout'u = "cihaz çevrimdışı"),
    // altyapı down değil — bu yüzden server-unreachable tetiklemez. Sadece gerçek
    // erişilemezlik sinyalleri (ağ hatası / 502 / 503) bir /health kontrolü tetikler.
    const isDown = (c) => c === 502 || c === 503;
    if (s === "FETCH_ERROR" || isDown(s) || isDown(os)) {
      window.dispatchEvent(new CustomEvent("server-unreachable"));
    }
  }

  if (
    result.error?.status === 401 &&
    !isAuthEndpoint(args.url) &&
    !args.skipRefresh
  ) {
    const holder = getAuthTokenHolder();
    if (!holder?.setNewToken) return result;

    // Paylaşılan (deduplike) refresh: aynı anda 401 alan N istek tek bir
    // /api/users/refresh çağrısına katılır. Doğrudan base(...) ile çağrılırsa
    // her istek kendi refresh'ini açar ve sunucuda 429'a yol açar.
    const refreshResult = await refreshAccessToken();

    if (!refreshResult?.token) {
      window.dispatchEvent(
        new CustomEvent("session-expired", { detail: { reason: "refresh_failed" } })
      );
      return result;
    }

    const newToken = refreshResult.token;
    const newUser = refreshResult.user;
    if (newToken) holder.setNewToken(newToken, newUser);

    result = await base(
      {
        ...args,
        headers: {
          ...(args.headers || {}),
          Authorization: `Bearer ${newToken}`,
        },
      },
      api,
      extraOptions
    );
  }

  return result;
};
